import React, { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { logException } from 'lib/errors'
import {
    numberize,
    roundNumberFieldTo,
} from 'lib/utils/formManipulators'
import { ExtendedFormikContext, Manipulator } from 'components/Forms/ExtendedFormik'

import { Field } from 'formik'
import Input from './Input'

const NumberField = ({
    onChange,
    onFocus,
    onBlur,
    decimals,
    ...rest
}) => {
    const { extendedActions: { manipulateField }, setFieldValue, setTouched } = useContext(ExtendedFormikContext)

    const { name } = rest

    // There was issue where invalid number value eg. "123.11.22" would persist in
    // the form field, but formik state had empty value.
    // So the state and rendered input did not match.
    // This occurs when the type is "number".
    //
    // By switching the type to "text" on blur event and then switching it back to "number"
    // on next render will clear the invalid number visually (to match the formik state).
    //
    // We still wan't to use the "number" type to make sure mobile devices renders correct numeric keyboard.
    const [type, setType] = useState('number')

    useEffect(() => {
        if (type !== 'number') {
            setType('number')
        }
    }, [type])

    const handleChange = async (e) => {
        const manipulators = manipulateField(setFieldValue, name, [
            Manipulator.create(roundNumberFieldTo(decimals)),
        ])

        // we need to call persist to keep the event available after manipulators
        // to be passed to onChange
        onChange && e.persist()

        await manipulators(e)
            .catch(e => logException(e))

        onChange && await onChange(e)
    }

    const handleBlur = async (e) => {
        const manipulators = manipulateField(setFieldValue, name, [
            Manipulator.create(numberize),
        ])

        // we need to call persist to keep the event available after manipulators
        // to be passed to onBlur
        onBlur && e.persist()

        await manipulators(e)
            .catch(e => logException(e))

        setType('text')

        setTouched({ [name]: true })

        onBlur && await onBlur(e)
    }

    const handleFocus = async (e) => {
        onFocus && await onFocus(e)
    }

    return (
        <Field
            type={type}
            step="any"
            component={Input}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            {...rest}
        />
    )
}

NumberField.propTypes = {
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
}

NumberField.defaultProps = {
    decimals: 2,
}

export default React.memo(NumberField)
